import { Box, Typography } from '@mui/material'
import { useMemo } from 'react'

import { housePartsIcons } from '@/assets/icons/house-parts'
import Button from '@/shared/components/Button'
import { getRetrofitDisplayName, retrofitToIcon } from '@/shared/utils/displayMappings'

import type { SelectedMeasuresProps } from './SelectedMeasures.types'

const SelectedMeasures = ({
  selectedMeasures,
  onCreatePackage,
  isLoading = false,
}: SelectedMeasuresProps) => {
  // Calculate totals from selected measures
  const calculations = useMemo(() => {
    if (selectedMeasures.length === 0) {
      return {
        totalRenovationCosts: 0,
        maxSubsidy: 0,
        totalCosts: 0,
      }
    }

    let totalRenovationCosts = 0
    let maxSubsidy = 0

    selectedMeasures.forEach(measure => {
      // Get mean renovation costs in cents
      const renovationCostsInCt = measure.economics.retrofitCostsInCt?.mean || 0
      totalRenovationCosts += renovationCostsInCt

      // Calculate subsidy based on subsidy rate
      const subsidyRate = measure.economics.subsidyRateInPct || 0
      const subsidyAmount = (renovationCostsInCt * subsidyRate) / 100
      maxSubsidy += subsidyAmount
    })

    const totalCosts = totalRenovationCosts - maxSubsidy

    return {
      totalRenovationCosts: totalRenovationCosts / 100, // Convert to euros
      maxSubsidy: maxSubsidy / 100, // Convert to euros
      totalCosts: totalCosts / 100, // Convert to euros
    }
  }, [selectedMeasures])

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }
  return (
    <Box
      sx={{
        borderRadius: {
          xs: '24px 24px 0 0', // Mobile and tablet
          lg: '24px', // Desktop
        },
        backgroundColor: '#FFFFFF',
        boxShadow: '0 -1px 9px 0 rgba(0, 0, 0, 0.12)',
        paddingTop: {
          xs: '16px', // Mobile/tablet
          lg: '24px', // Desktop
        },
        paddingX: '24px', // Same for both
        paddingBottom: {
          xs: '24px', // Mobile/tablet
          lg: '18px', // Desktop
        },
      }}
    >
      {/* Header: Title and Button on mobile, Title only on desktop */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: {
            xs: '16px', // Mobile/tablet
            lg: selectedMeasures.length === 0 ? '27px' : '16px', // Desktop
          },
        }}
      >
        <Typography
          component="h2"
          sx={{
            fontWeight: 600,
            fontSize: '13px',
            lineHeight: '20px',
            color: '#1A1D24',
          }}
        >
          Ausgewählte Sanierungsmaßnahmen
        </Typography>

        {/* Button on mobile/tablet only (next to title) */}
        <Box
          sx={{
            display: {
              xs: 'block', // Show on mobile/tablet
              lg: 'none', // Hide on desktop
            },
          }}
        >
          <Button
            height="38px"
            radius="9px"
            sx={{
              fontSize: '12px',
              paddingX: 1.5,
            }}
            disabled={selectedMeasures.length === 0 || isLoading}
            onClick={onCreatePackage}
          >
            {isLoading ? 'Wird erstellt...' : 'Paket erstellen'}
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          display: selectedMeasures.length === 0 ? 'none' : 'block',
          marginBottom: {
            xs: '16px', // Mobile/tablet
            lg: '32px', // Desktop
          },
        }}
      >
        {/* Desktop: 2-column grid, Mobile: Horizontally scrollable */}
        <Box
          sx={{
            // Desktop: Grid layout
            display: {
              xs: 'flex', // Flex for mobile scrolling
              lg: 'grid', // Grid for desktop
            },
            gridTemplateColumns: {
              lg: 'repeat(2, 1fr)', // 2 columns on desktop
            },
            gap: '16px', // 16px gap between items and rows
            // Mobile: Horizontal scroll
            overflowX: {
              xs: 'auto', // Enable horizontal scroll on mobile
              lg: 'visible', // No scroll on desktop
            },
            // Hide scrollbar but keep functionality
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none', // Firefox
          }}
        >
          {selectedMeasures.map(measure => (
            <Box
              key={measure.retrofitName}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <Box
                component="img"
                src={housePartsIcons[retrofitToIcon[measure.retrofitName]]}
                alt={getRetrofitDisplayName(measure.retrofitName)}
                sx={{
                  width: '32px',
                  height: '32px',
                  flexShrink: 0,
                  objectFit: 'contain',
                }}
              />
              <Typography
                sx={{
                  fontSize: '10px',
                  fontWeight: 500,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {getRetrofitDisplayName(measure.retrofitName)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Cost items row */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: {
            xs: 'flex-start', // Left-aligned on mobile/tablet
            lg: 'space-between', // Spread on desktop
          },
          gap: {
            xs: '24px', // Gap between items on mobile/tablet
            lg: 0, // No gap on desktop (space-between handles it)
          },
          width: '100%',
          marginBottom: {
            xs: 0, // Mobile/tablet
            lg: '43px', // Desktop
          },
        }}
      >
        {/* Sanierungskosten */}
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '10px',
              color: '#464A54',
              marginBottom: {
                xs: '15px', // Mobile/tablet
                lg: '24px', // Desktop
              },
            }}
          >
            Sanierungskosten
          </Typography>
          <Typography
            sx={{
              height: '21px',
              fontWeight: selectedMeasures.length === 0 ? 500 : 600,
              fontSize: selectedMeasures.length === 0 ? '11px' : '14px',
              color: selectedMeasures.length === 0 ? '#74777F' : 'inherit',
            }}
          >
            {selectedMeasures.length === 0
              ? '--'
              : formatCurrency(calculations.totalRenovationCosts)}
          </Typography>
        </Box>

        {/* max. Förderung */}
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '10px',
              color: '#464A54',
              marginBottom: {
                xs: '15px', // Mobile/tablet
                lg: '24px', // Desktop
              },
            }}
          >
            max. Förderung
          </Typography>
          <Typography
            sx={{
              height: '21px',
              fontWeight: selectedMeasures.length === 0 ? 500 : 600,
              fontSize: selectedMeasures.length === 0 ? '11px' : '14px',
              color: selectedMeasures.length === 0 ? '#74777F' : 'inherit',
            }}
          >
            {selectedMeasures.length === 0 ? '--' : formatCurrency(calculations.maxSubsidy)}
          </Typography>
        </Box>

        {/* Gesamtkosten */}
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '10px',
              color: '#464A54',
              marginBottom: {
                xs: '15px', // Mobile/tablet
                lg: '24px', // Desktop
              },
            }}
          >
            Gesamtkosten
          </Typography>
          <Typography
            sx={{
              height: '21px',
              fontWeight: selectedMeasures.length === 0 ? 500 : 600,
              fontSize: selectedMeasures.length === 0 ? '11px' : '14px',
              color: selectedMeasures.length === 0 ? '#74777F' : 'inherit',
            }}
          >
            {selectedMeasures.length === 0 ? '--' : formatCurrency(calculations.totalCosts)}
          </Typography>
        </Box>
      </Box>

      {/* Button on desktop only (below items) */}
      <Box
        sx={{
          display: {
            xs: 'none', // Hide on mobile/tablet
            lg: 'block', // Show on desktop
          },
        }}
      >
        <Button
          fullWidth
          disabled={selectedMeasures.length === 0 || isLoading}
          onClick={onCreatePackage}
        >
          {isLoading ? 'Wird erstellt...' : 'Paket erstellen'}
        </Button>
      </Box>
    </Box>
  )
}

export default SelectedMeasures
